import { useState } from "react";
import { UploadCloud, Check } from "lucide-react";
import { useStore, api } from "@/context/StoreContext";

export default function PaymentFollowUp({ order, phone, onUpdated }) {
  const { t, toast } = useStore();
  const [ref, setRef] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const status = order.payment_status;

  const post = async (payload, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.order_number}/payment-submitted`, { phone, ...payload });
      const pc = data?.payway_check;
      if (pc && pc.checked) {
        onUpdated(data);
        if (pc.stock_unavailable) {
          toast(t("confirm.outOfStock"));
          return false;
        }
        if (!pc.approved) {
          toast(`${t("confirm.paywayUnpaid")} ${pc.transaction_status}${pc.payway_message ? ` — ${pc.payway_message}` : ""}`);
          return false;
        }
      }
      onUpdated(data);
      toast(data?.payment_status === "confirmed" ? t("confirm.paywayPaidOk") : okMsg);
      return true;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast(typeof d === "string" ? d : t("toast.orderFail"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const markSubmitted = () => post({}, t("confirm.submittedOk"));

  const sendInfo = async () => {
    if (!ref.trim() && !receipt) return;
    const payload = { reference: ref.trim() };
    if (receipt) {
      payload.receipt_name = receipt.name;
      payload.receipt_b64 = receipt.b64;
    }
    const ok = await post(payload, t("confirm.sentOk"));
    if (ok) {
      setRef("");
      setReceipt(null);
      setSent(true);
    }
  };

  const pickReceipt = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast(t("confirm.receiptTooBig"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setReceipt({ name: file.name, b64: String(reader.result) });
    reader.readAsDataURL(file);
  };

  if (status === "confirmed") return null;

  if (status === "pending") {
    return (
      <div className="followup-card" data-testid="completed-payment-panel">
        <button className="checkout-btn btn-paid" data-testid="completed-payment-button" onClick={markSubmitted} disabled={busy}>
          {t("confirm.completedPayment")}
        </button>
        <p className="small-note">{t("confirm.completedHint")}</p>
      </div>
    );
  }

  return (
    <div className="followup-card submitted" data-testid="submitted-panel">
      <div className="submitted-msg" data-testid="submitted-message">
        <Check size={16} />
        <span>{t("confirm.submittedMsg")}</span>
      </div>
      {sent && (
        <p className="small-note" data-testid="info-sent-note" style={{ color: "#1e7d4f", fontWeight: 700 }}>
          ✓ {t("confirm.sentOk")}
        </p>
      )}
      <input
        className="ref-input"
        data-testid="reference-input"
        value={ref}
        onChange={(e) => setRef(e.target.value)}
        placeholder={t("confirm.refPlaceholder")}
      />
      <label className="receipt-label" data-testid="receipt-label">
        <UploadCloud size={15} /> {receipt ? receipt.name : t("confirm.receiptLabel")}
        <input type="file" accept="image/*" data-testid="receipt-input" onChange={pickReceipt} />
      </label>
      <button
        className="checkout-btn btn-track"
        data-testid="send-info-button"
        onClick={sendInfo}
        disabled={busy || (!ref.trim() && !receipt)}
      >
        {t("confirm.sendInfo")}
      </button>
    </div>
  );
}
